import { useEffect, useState } from "react";
import { platformApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Edit2, Eye, Loader2 } from "lucide-react";

const DARK_CARD = "bg-slate-900 border border-slate-800 rounded-[8px]";

export default function AdminEmailTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await platformApi.get("/platform/email-templates");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-email-templates-page">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-rose-400">// Platform-wide</div>
        <h1 className="text-3xl font-black mt-1 flex items-center gap-3">
          <Mail className="h-7 w-7 text-rose-400" /> Email templates
        </h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          One set of templates is sent to every tenant. Tenant managers no longer have an in-app
          editor (Phase 31). Edits made here propagate to the next outgoing notification —
          no per-tenant overrides, no migration steps.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading templates…</div>
      ) : (
        <div className={`${DARK_CARD} overflow-hidden`}>
          <table className="w-full text-sm">
            <thead className="bg-slate-950/50 text-slate-400">
              <tr>
                <th className="text-left px-4 py-2 w-[40%]">Template</th>
                <th className="text-left px-4 py-2">Subject</th>
                <th className="text-left px-4 py-2 w-[120px]">Enabled</th>
                <th className="text-left px-4 py-2 w-[80px]">Ver</th>
                <th className="text-right px-4 py-2 w-[140px]"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.code} className="border-t border-slate-800" data-testid={`admin-email-tpl-row-${t.code}`}>
                  <td className="px-4 py-2">
                    <div className="font-bold">{t.name || t.code}</div>
                    <div className="mono text-[10px] uppercase tracking-wider text-slate-500">{t.code}</div>
                  </td>
                  <td className="px-4 py-2 text-slate-300 max-w-[300px] truncate">{t.subject || <span className="text-slate-600 italic">—</span>}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] uppercase mono tracking-widest px-2 py-0.5 rounded border ${t.enabled ? "bg-emerald-900/40 text-emerald-300 border-emerald-700" : "bg-slate-800 text-slate-500 border-slate-700"}`}>
                      {t.enabled ? "On" : "Off"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-400 mono">{t.version}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(t)}
                      className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                      data-testid={`admin-email-tpl-edit-${t.code}`}
                    >
                      <Edit2 className="h-3 w-3 mr-1" /> Edit
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500 italic">No templates yet — run the Phase 31 migration script.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <EditDialog template={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function EditDialog({ template, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyMd, setBodyMd] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  useEffect(() => {
    if (template) {
      setName(template.name || template.code);
      setSubject(template.subject || "");
      setBodyMd(template.body_markdown || "");
      setEnabled(template.enabled !== false);
    }
  }, [template]);

  const save = async () => {
    if (!template) return;
    setBusy(true);
    try {
      await platformApi.put(`/platform/email-templates/${template.code}`, {
        name, subject, body_markdown: bodyMd, enabled,
      });
      toast.success("Template saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!template} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-slate-900 border-slate-700 text-slate-100" data-testid="admin-email-tpl-dialog">
        <DialogHeader>
          <DialogTitle>{template?.name || template?.code}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Code <code className="text-slate-200">{template?.code}</code> · Version {template?.version}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   className="bg-slate-800 border-slate-700 text-slate-100"
                   data-testid="admin-email-tpl-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                   className="bg-slate-800 border-slate-700 text-slate-100"
                   data-testid="admin-email-tpl-subject" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Body (markdown)</Label>
            <Textarea value={bodyMd} onChange={(e) => setBodyMd(e.target.value)}
                      rows={18}
                      className="bg-slate-800 border-slate-700 text-slate-100 font-mono text-sm"
                      data-testid="admin-email-tpl-body" />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="admin-email-tpl-enabled" />
            <Label className="text-sm">Send this notification</Label>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="admin-email-tpl-save">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
